/* No external libraries. This file also runs in the theme editor. */
(() => {
  class KtHeader extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      const { signal } = this.controller;
      this.menu = this.querySelector('details');
      if (!this.menu) return;
      this.summary = this.menu.querySelector('summary');
      this.menu.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.menu.open));
        document.body.classList.toggle('kt-menu-open', this.menu.open);
        if (this.menu.open) this.menu.querySelector('[data-menu-close]').focus();
      }, { signal });
      this.querySelector('[data-menu-close]').addEventListener('click', () => this.close(), { signal });
      this.addEventListener('keydown', event => {
        if (!this.menu.open) return;
        if (event.key === 'Escape') { event.preventDefault(); this.close(); }
        if (event.key === 'Tab') {
          const items = [...this.menu.querySelectorAll('.kt-menu__panel a, .kt-menu__panel button')];
          const first = items[0], last = items.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      }, { signal });
      window.addEventListener('resize', () => { if (window.innerWidth >= 990 && this.menu.open) this.close(); }, { signal });
    }
    close() { this.menu.open = false; this.summary.focus(); }
    disconnectedCallback() { this.controller?.abort(); document.body.classList.remove('kt-menu-open'); }
  }

  class KtHero extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (!this.slides.length) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.reduced.matches || this.dataset.autoplay !== 'true';
      this.visible = true; this.hovered = false; this.focused = false; this.index = 0;
      this.order = this.slides.map((_, index) => index);
      if (!window.Shopify?.designMode && this.dataset.mode === 'shuffle') {
        for (let i = this.order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [this.order[i], this.order[j]] = [this.order[j], this.order[i]]; }
      } else if (!window.Shopify?.designMode && this.dataset.mode === 'random_start') this.index = Math.floor(Math.random() * this.slides.length);
      this.controls = this.querySelector('[data-hero-controls]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.controls.hidden = this.slides.length < 2 && !this.querySelector('video');
      for (const [selector, direction] of [['[data-prev]', -1], ['[data-next]', 1]]) {
        const button = this.querySelector(selector); button.disabled = this.slides.length < 2;
        button.addEventListener('click', () => { this.paused = true; this.show(this.index + direction); }, { signal });
      }
      this.pauseButton.addEventListener('click', () => { this.paused = !this.paused; this.sync(); }, { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.sync(); } }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, { signal });
      this.addEventListener('focusin', event => { this.focused = !this.controls.contains(event.target); this.sync(); }, { signal });
      this.addEventListener('focusout', () => { this.focused = false; this.sync(); }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.reduced.addEventListener('change', () => { if (this.reduced.matches) this.paused = true; this.sync(); }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const target = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (target >= 0) { this.paused = true; this.show(this.order.indexOf(target)); }
      }, { signal });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }, { threshold: .05 });
      this.observer.observe(this);
      this.show(this.index);
    }
    show(index) {
      this.index = (index + this.order.length) % this.order.length;
      const current = this.order[this.index];
      this.slides.forEach((slide, i) => {
        const active = i === current; slide.classList.toggle('is-active', active);
        slide.inert = !active; slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelector('video')?.pause();
      });
      this.querySelector('[data-hero-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.sync();
    }
    sync() {
      clearTimeout(this.timer);
      const running = !this.paused && !this.reduced.matches && this.visible && !document.hidden && !this.hovered && !this.focused;
      this.pauseButton.setAttribute('aria-label', this.paused ? '自動再生を開始' : '自動再生を停止');
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (running && index === this.order[this.index]) {
          video.muted = true; video.playsInline = true;
          video.play().then(() => { if (slide.classList.contains('is-active') && !this.paused) video.classList.add('is-playing'); }).catch(() => video.classList.remove('is-playing'));
        } else video.pause();
      });
      if (running && this.slides.length > 1) this.timer = setTimeout(() => this.show(this.index + 1), Number(this.dataset.interval) || 8000);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); this.observer?.disconnect(); this.querySelectorAll('video').forEach(video => video.pause()); }
  }
  if (!customElements.get('kt-header')) customElements.define('kt-header', KtHeader);
  if (!customElements.get('kt-hero')) customElements.define('kt-hero', KtHero);

  // Recheck the server-rendered cart before any theme checkout action.
  // This is a storefront guard; it is not a checkout/server validation function.
  const applyCartState = () => {
    const status = document.querySelector('[data-kt-cart-status]');
    if (!status) return;
    const blocked = status.dataset.blocked === 'true';
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(button => {
      if (blocked) { if (!button.disabled) button.disabled = true; if (button.dataset.ktBlocked !== 'true') button.dataset.ktBlocked = 'true'; }
      else if (button.dataset.ktBlocked === 'true') { button.disabled = false; delete button.dataset.ktBlocked; }
    });
  };
  new MutationObserver(applyCartState).observe(document.body, { childList: true, subtree: true });
  applyCartState();
  let checking = false;
  const checkBeforeCheckout = async event => {
    const submit = event.type === 'submit';
    const trigger = submit ? event.submitter : event.target.closest('a[href], button[name="checkout"], input[name="checkout"]');
    if (!trigger) return;
    const url = trigger.tagName === 'A' ? new URL(trigger.href, location.origin) : null;
    if (submit ? trigger.name !== 'checkout' : (trigger.name !== 'checkout' && !url?.pathname.match(/\/checkout\/?$/))) return;
    if (submit && event.target.dataset.ktCheckoutApproved === 'true') { delete event.target.dataset.ktCheckoutApproved; return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    const form = trigger.form || (submit ? event.target : null);
    const originalDisabled = trigger.disabled;
    if ('disabled' in trigger) trigger.disabled = true;
    let blocked = true;
    try {
      // Preserve cart notes/attributes before checking the freshest cart state.
      if (form) {
        const fields = new FormData(form); const attributes = {};
        for (const [key, value] of fields.entries()) { const match = key.match(/^attributes\[(.+)\]$/); if (match) attributes[match[1]] = value; }
        const note = fields.get('note');
        if (note !== null || Object.keys(attributes).length) {
          const saved = await fetch(window.routes.cart_update_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...(note !== null ? { note } : {}), attributes }) });
          if (!saved.ok) throw new Error('cart save failed');
        }
      }
      const root = window.Shopify?.routes?.root || '/';
      const result = await fetch(root + '?section_id=kt-cart-check&_=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' });
      if (!result.ok) throw new Error('cart check failed');
      const doc = new DOMParser().parseFromString(await result.text(), 'text/html');
      const state = doc.querySelector('[data-kt-cart-status]');
      if (!state || !['true', 'false'].includes(state.dataset.blocked)) throw new Error('missing cart state');
      blocked = state.dataset.blocked === 'true';
      if (blocked) {
        const current = document.querySelector('[data-kt-cart-status]');
        if (current) { current.replaceWith(state); state.hidden = false; state.focus(); }
        else location.assign(window.routes.cart_url);
      } else {
        // Derive both warehouse attributes from the freshly rendered cart.
        // Reset the type and date when a preorder is replaced with ordinary products.
        if (!['true', 'false'].includes(state.dataset.preorder)) throw new Error('missing preorder state');
        const orderType = state.dataset.preorder === 'true' ? 'preorder' : 'normal';
        const shipDate = state.dataset.preorder === 'true' ? state.dataset.plannedShipDate : '';
        if (state.dataset.preorder === 'true' && (!/^\d{4}-\d{2}-\d{2}$/.test(shipDate) ||
          new Date(shipDate + 'T00:00:00Z').toISOString().slice(0, 10) !== shipDate)) throw new Error('invalid shipping date');
        const warehouseAttributes = { planned_ship_date: shipDate, order_type: orderType };
        const saved = await fetch(root + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: warehouseAttributes })
        });
        if (!saved.ok) throw new Error('shipping date save failed');
        const savedCart = await saved.json();
        if ((savedCart.attributes?.planned_ship_date || '') !== shipDate) throw new Error('shipping date not saved');
        if (savedCart.attributes?.order_type !== orderType) throw new Error('order type not saved');
        if (form) {
          // A native form submission must not overwrite either verified attribute.
          for (const [key, value] of Object.entries(warehouseAttributes)) {
            Array.from(form.elements).filter(input => input.name === `attributes[${key}]`).forEach(input => { input.value = value; });
          }
          form.dataset.ktCheckoutApproved = 'true'; trigger.disabled = false; form.requestSubmit(trigger);
        } else location.assign(trigger.href);
      }
    } catch {
      let error = document.querySelector('.kt-cart-gate-error');
      if (!error) { error = document.createElement('p'); error.className = 'kt-cart-gate-error'; error.role = 'alert'; trigger.after(error); }
      error.textContent = 'カートの確認ができませんでした。通信状況を確認し、もう一度お試しください。';
      blocked = false;
    } finally { checking = false; if ('disabled' in trigger) trigger.disabled = blocked || originalDisabled === true; applyCartState(); }
  };
  document.addEventListener('click', checkBeforeCheckout, true);
  document.addEventListener('submit', checkBeforeCheckout, true);
})();
